import React, {
  ButtonHTMLAttributes,
  CSSProperties,
  FC,
  InputHTMLAttributes,
  ReactElement,
  ReactNode,
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { styled } from "@mui/system";
import {
  ButtonProps as MuiButtonProps,
  Dialog as MuiDialog,
  DialogProps as MuiDialogProps,
  IconButtonProps as MuiIconButtonProps,
  Menu as MuiMenu,
  MenuProps as MuiMenuProps,
  SkeletonProps,
  Tooltip as MuiTooltip,
  TooltipProps as MuiTooltipProps,
} from "@mui/material";
import { Transition, TransitionStatus } from "react-transition-group";
import { HUBSPOT_SANS } from "./theme";
import { HsCheckIcon, HsCloseIcon, HsDashIcon, HsInfoIcon, HsLeftIcon, HsRightIcon, HsSearchIcon } from "./icons";
import "./hubspot-kit.css";

/**
 * HubSpot's current UI, for HubSpot orgs. It mirrors components/OutreachUi
 * export for export, with the same names and props, so a screen can take
 * either kit; components/CrmUi picks one from the org's CRM.
 *
 * Every value comes from the live captures in docs/hubspot-ui-reference
 * (see hubspot-kit.css, which names the capture behind each part), and
 * app/scripts/hubspot-visual diffs each part against them pixel for pixel.
 * Lexend Deca at 300 for text, #333 ink, #CCC hairlines, #00494B teal
 * primary pills, #8A8A8A outlined secondary pills, 16px-cornered white cards
 * on HubSpot's #F5F5F5 page, a 2px #016DE1 focus outline.
 *
 * The --ou-* custom properties keep their names, so the screens' own styled
 * parts (which read OU.*) follow whichever kit's page they sit in.
 */

export { HUBSPOT_SANS };
/** Same name as the Outreach kit's font constant, so screens can swap kits. */
export const OUTREACH_SANS = HUBSPOT_SANS;

export const OU = {
  ink: "var(--ou-ink, #333333)",
  muted: "var(--ou-muted, #666666)",
  brand: "var(--ou-brand, #006162)",
  brandHover: "var(--ou-brand-hover, #00494b)",
  primaryBg: "var(--ou-primary-bg, #00494b)",
  primaryBgHover: "var(--ou-primary-bg-hover, #007c7d)",
  soft: "var(--ou-soft, #f0f0f0)",
  softHover: "var(--ou-soft-hover, #e6e6e6)",
  rule: "var(--ou-rule, #cccccc)",
  surface: "var(--ou-surface, #ffffff)",
  wash: "var(--ou-wash, #f5f5f5)",
  inputBorder: "var(--ou-input-border, #333333)",
  inputBorderHover: "var(--ou-input-border-hover, #333333)",
  disabledText: "var(--ou-disabled-text, #8a8a8a)",
  switchOn: "var(--ou-switch-on, #00494b)",
  switchOff: "var(--ou-switch-off, #cccccc)",
  shadow: "var(--ou-shadow, 0 1px 8px 0 rgba(20, 20, 20, 0.08))",
  bone: "var(--ou-bone, #f0f0f0)",
  tableBg: "var(--ou-table-bg, #ffffff)",
  rowHover: "var(--ou-row-hover, #e6e6e6)",
  divider: "var(--ou-divider, #cccccc)",
};

const cx = (...names: (string | false | null | undefined)[]): string => names.filter(Boolean).join(" ");

/* ── page ── */

export const OuPage = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 900px;
  font-family: ${HUBSPOT_SANS};
  font-size: 14px;
  font-weight: 300;
  line-height: 24px;
  color: var(--hs-text, #333333);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  a {
    color: var(--hs-link, #006162);
    font-weight: 600;
    text-decoration: underline transparent;
    text-underline-offset: 24%;
    transition: 150ms ease-out;
  }

  a:hover {
    color: var(--hs-link-hover, #00494b);
    text-decoration: underline var(--hs-link-hover, #00494b);
  }
`;

/* ── cards ── */

export const OuCard = styled("section")`
  box-sizing: border-box;
  min-width: 0;
  padding: 24px;
  border: 1px solid var(--hs-border, #cccccc);
  border-radius: 16px;
  background: var(--hs-surface, #ffffff);
  box-shadow: var(--hs-shadow-card, 0 1px 8px 0 rgba(20, 20, 20, 0.08));
  color: var(--hs-text, #333333);

  @media (max-width: 600px) {
    padding: 20px 16px;
  }
`;

const HeaderRow = styled("div")`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas:
    "title aside"
    "desc desc";
  column-gap: 24px;
  row-gap: 4px;
  align-items: center;

  > h2,
  > h3 {
    grid-area: title;
  }

  > p {
    grid-area: desc;
  }

  @media (max-width: 600px) {
    column-gap: 16px;
  }
`;

const HeaderAside = styled("div")`
  grid-area: aside;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
`;

/** Page title: the dialog heading's 20/24 at 500. */
export const OuTitle = styled("h2")`
  margin: 0;
  font: 500 20px/24px ${HUBSPOT_SANS};
  color: var(--hs-text, #333333);
  letter-spacing: 0;
`;

/** Card title: 16/20 at 600 (record card headers). */
export const OuSectionTitle = styled("h3")`
  margin: 0;
  font: 600 16px/20px ${HUBSPOT_SANS};
  color: var(--hs-text, #333333);
  letter-spacing: 0;
`;

export const OuDescription = styled("p")`
  margin: 0;
  font: 300 14px/24px ${HUBSPOT_SANS};
  color: var(--hs-text-secondary, #666666);
  max-width: 640px;
`;

export const OuCardHeader: FC<{
  title: ReactNode;
  description?: ReactNode;
  aside?: ReactNode;
  /** "page" for the page's own title, "section" for a card within it. */
  level?: "page" | "section";
}> = ({ title, description, aside, level = "section" }) => (
  <HeaderRow>
    {level === "page" ? <OuTitle>{title}</OuTitle> : <OuSectionTitle>{title}</OuSectionTitle>}
    {aside && <HeaderAside>{aside}</HeaderAside>}
    {description && <OuDescription>{description}</OuDescription>}
  </HeaderRow>
);

/* ── setting rows ── */

const RowWrap = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 16px 0;
  border-top: 1px solid var(--hs-border, #cccccc);

  @media (max-width: 600px) {
    flex-wrap: wrap;
    gap: 12px 16px;

    > div:first-of-type {
      flex: 1 1 200px;
    }

    > div:last-of-type {
      flex: 1 0 auto;
    }
  }
`;

const RowText = styled("div")`
  flex: 1 1 280px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RowLabel = styled("label")`
  margin: 0;
  font: 600 14px/24px ${HUBSPOT_SANS};
  color: var(--hs-text, #333333);
`;

const RowHelper = styled("span")`
  font: 300 12px/18px ${HUBSPOT_SANS};
  color: var(--hs-text-secondary, #666666);
  max-width: 520px;
`;

const RowControl = styled("div")`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
`;

export const OuSettingRow: FC<{ label: ReactNode; helper?: ReactNode; htmlFor?: string; children: ReactNode }> = ({ label, helper, htmlFor, children }) => (
  <RowWrap>
    <RowText>
      <RowLabel htmlFor={htmlFor}>{label}</RowLabel>
      {helper && <RowHelper>{helper}</RowHelper>}
    </RowText>
    <RowControl>{children}</RowControl>
  </RowWrap>
);

/* ── fields ── */

/** Form label: 14/24 at 600 above the field. */
export const OuFieldLabel = styled("label")`
  display: block;
  margin: 0 0 4px;
  font: 600 14px/24px ${HUBSPOT_SANS};
  color: var(--hs-text, #333333);
`;

/* HubSpot's form field (select-closed.json, "form" use): 42px, 16/24 at 300,
   a #333 outline with 6px corners, the blue focus outline drawn inside. */
export const OuControl = styled("div")`
  min-width: 0;

  .MuiFormControl-root,
  .MuiTextField-root {
    width: 100%;
    margin: 0;
  }

  .MuiOutlinedInput-root {
    height: 42px;
    border-radius: 6px;
    background: var(--hs-surface, #ffffff);
    font: 300 16px/24px ${HUBSPOT_SANS};
    color: var(--hs-text, #333333);
    transition: 150ms ease-out;
  }

  .MuiOutlinedInput-input {
    box-sizing: border-box;
    height: 42px;
    padding: 8px 16px;
    font: 300 16px/24px ${HUBSPOT_SANS};
    color: var(--hs-text, #333333);
  }

  .MuiOutlinedInput-input::placeholder {
    color: var(--hs-text-secondary, #666666);
    opacity: 1;
  }

  input[type="number"] {
    font-variant-numeric: tabular-nums;
  }

  .MuiOutlinedInput-notchedOutline {
    border: 1px solid var(--hs-border-input, #333333);
    border-radius: 6px;
  }

  .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: var(--hs-border-input, #333333);
  }

  .MuiOutlinedInput-root.Mui-focused {
    outline: 2px solid var(--hs-focus, #016de1);
    outline-offset: -2px;
    box-shadow: inset 0 0 0 2px var(--hs-focus-inner, #ffffff);
  }

  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border: 1px solid transparent;
  }

  .MuiOutlinedInput-root.Mui-disabled {
    background: var(--hs-fill-disabled, #f5f5f5);
  }

  .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline {
    border-color: var(--hs-border-disabled, #e6e6e6);
  }
`;

export const OuField: FC<{ label: ReactNode; htmlFor?: string; children: ReactNode; className?: string }> = ({ label, htmlFor, children, className }) => (
  <OuControl className={className}>
    <OuFieldLabel htmlFor={htmlFor}>{label}</OuFieldLabel>
    {children}
  </OuControl>
);

export interface OuSelectOption {
  value: string;
  label: ReactNode;
}

/* ── menus, popovers ── */

/**
 * HubSpot's dropdown surface on MUI's Menu: the white 16px-cornered popover
 * with its 24/48 shadow, 40px items, #F0F0F0 hover. `flush` drops the list's
 * 8px top and bottom padding (select menus start at their first option).
 */
export const HsMenu: FC<MuiMenuProps & { flush?: boolean }> = ({ flush, PaperProps, MenuListProps, ...rest }) => (
  <MuiMenu
    {...rest}
    PaperProps={{ ...PaperProps, className: cx("hs-popover", "hs-mui-popover", PaperProps?.className), elevation: 0 }}
    MenuListProps={{ ...MenuListProps, className: cx("hs-menu", flush && "hs-menu--flush", MenuListProps?.className) }}
  />
);

/** HubSpot's select (a "form" button with the 12px caret) opening an HsMenu. */
export const OuSelect: FC<{
  value: string;
  onChange: (value: string) => void;
  options: OuSelectOption[];
  id?: string;
  disabled?: boolean;
  "aria-label"?: string;
  placeholder?: ReactNode;
  size?: "md" | "sm";
}> = ({ value, onChange, options, id, disabled, "aria-label": ariaLabel, placeholder, size = "md" }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const listId = useId();
  const current = options.find((o) => o.value === value);
  return (
    <>
      <button
        type="button"
        id={id}
        className={cx("hs-select", "hs-focusable", size === "sm" && "hs-select--sm")}
        aria-haspopup="listbox"
        aria-expanded={!!anchor}
        aria-controls={anchor ? listId : undefined}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        <span className="hs-select__label">{current ? current.label : <span className="hs-select__placeholder">{placeholder ?? "Select"}</span>}</span>
        <span className={cx("hs-caret", size === "sm" ? "hs-caret--md" : "hs-caret--lg")} aria-hidden="true" />
      </button>
      <HsMenu
        flush
        id={listId}
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ style: { minWidth: anchor?.offsetWidth, marginTop: 4 } }}
        MenuListProps={{ role: "listbox" }}
      >
        {options.map((o) => (
          <li key={o.value} role="none">
            <button
              type="button"
              role="option"
              aria-selected={o.value === value}
              className={cx("hs-menu__item", "hs-menu__item--option", o.value === value && "is-selected")}
              onClick={() => {
                onChange(o.value);
                setAnchor(null);
              }}
            >
              {o.label}
            </button>
          </li>
        ))}
      </HsMenu>
    </>
  );
};

/** A popover box, optionally with HubSpot's arrow (28px wide, 14px deep). */
export const HsPopover = forwardRef<
  HTMLDivElement,
  { arrow?: "top" | "bottom"; arrowLeft?: number; padded?: boolean; children: ReactNode; className?: string; style?: CSSProperties } & Record<`data-${string}`, string>
>(function HsPopover({ arrow, arrowLeft = 16, padded = true, children, className, style, ...rest }, ref) {
  return (
    <div ref={ref} className={cx("hs-popover", className)} style={style} {...rest}>
      <div className={padded ? "hs-popover__body" : undefined}>{children}</div>
      {arrow && (
        <svg className={cx("hs-popover__arrow", `hs-popover__arrow--${arrow}`)} style={{ left: arrowLeft }} viewBox="0 0 28 28" aria-hidden="true">
          <path d="M0,0 H28 L14,14 Z" />
          <path d="M0,0 H28 L14,14 Z" />
        </svg>
      )}
    </div>
  );
});

/* ── toggle (UNVERIFIED: no HubSpot toggle was captured) ── */

export const OuSwitch: FC<{
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  saving?: boolean;
  id?: string;
  "aria-label"?: string;
}> = ({ checked, onChange, disabled, saving, id, "aria-label": ariaLabel }) => (
  <label className="hs-toggle" data-saving={saving ? "true" : undefined}>
    <input
      type="checkbox"
      role="switch"
      id={id}
      checked={checked}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-busy={saving || undefined}
      onChange={(e) => {
        if (saving) return;
        onChange(e.target.checked);
      }}
    />
    <span className="hs-toggle__track" aria-hidden="true">
      <span className="hs-toggle__knob" />
    </span>
  </label>
);

/* ── buttons ── */

/* size: "small" is HubSpot's 32px button (12/14 at 300); anything else the
   40px one (14/18 at 600) used in dialogs and forms. */
type OuButtonProps = Omit<MuiButtonProps, "variant" | "color"> & { loading?: boolean; icon?: ReactNode; pressed?: boolean };

/* The DOM attributes a kit button passes on; MUI-only props are dropped. */
const buttonAttrs = (p: OuButtonProps): ButtonHTMLAttributes<HTMLButtonElement> => {
  const { onClick, onMouseDown, onFocus, onBlur, type, id, title, style, tabIndex, form, name, value } = p as ButtonHTMLAttributes<HTMLButtonElement>;
  const aria = Object.fromEntries(Object.entries(p).filter(([k]) => k.startsWith("aria-") || k.startsWith("data-")));
  return { onClick, onMouseDown, onFocus, onBlur, type: type ?? "button", id, title, style, tabIndex, form, name, value: value as string | undefined, ...aria };
};

const kitButton = (use: "primary" | "secondary"): FC<OuButtonProps> => {
  const Button: FC<OuButtonProps> = (props) => {
    const { loading, icon, startIcon, endIcon, children, disabled, className, size, pressed } = props;
    return (
      <button
        {...buttonAttrs(props)}
        className={cx("hs-btn", `hs-btn--${use}`, size !== "small" && "hs-btn--lg", "hs-focusable", className)}
        disabled={disabled || loading}
        aria-pressed={pressed}
        aria-busy={loading || undefined}
        data-loading={loading ? "true" : undefined}
      >
        {loading ? <span className="hs-spinner" aria-hidden="true" /> : icon ?? startIcon}
        {children != null && children !== false && <span className="hs-btn__label">{children}</span>}
        {endIcon}
      </button>
    );
  };
  Button.displayName = use === "primary" ? "HsPrimaryButton" : "HsSecondaryButton";
  return Button;
};

export const OuPrimaryButton = kitButton("primary");
export const OuSecondaryButton = kitButton("secondary");

/** A button that opens a menu: its label, then the caret 4px after it. */
export const HsDropdownButton: FC<OuButtonProps & { use?: "primary" | "secondary" | "transparent" }> = (props) => {
  const { use = "primary", size = "small", children, className, disabled } = props;
  return (
    <button
      {...buttonAttrs(props)}
      className={cx("hs-btn", `hs-btn--${use}`, size !== "small" && "hs-btn--lg", "hs-focusable", className)}
      disabled={disabled}
      aria-haspopup="true"
    >
      <span className="hs-btn__label">{children}</span>
      <span className={cx("hs-caret", (size !== "small" || use === "transparent") && "hs-caret--md")} aria-hidden="true" />
    </button>
  );
};

const FooterWrap = styled("div")`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin: 8px -24px -24px;
  padding: 24px;
  border-top: 1px solid var(--hs-border, #cccccc);
  border-radius: 0 0 16px 16px;

  @media (max-width: 600px) {
    margin: 4px -16px -20px;
    padding: 16px;
    flex-wrap: wrap;
  }
`;

const FooterStatus = styled("div")`
  flex: 1 1 auto;
  min-width: 0;
  font: 300 12px/18px ${HUBSPOT_SANS};
  color: var(--hs-text-secondary, #666666);
`;

export const OuActions = styled("div")`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  flex: 0 0 auto;
  margin-left: auto;
`;

export const OuCardFooter: FC<{ status?: ReactNode; children: ReactNode }> = ({ status, children }) => (
  <FooterWrap>
    <FooterStatus role="status" aria-live="polite">
      {status}
    </FooterStatus>
    <OuActions>{children}</OuActions>
  </FooterWrap>
);

/* ── tags, alerts, empty states (UNVERIFIED: none on the captured pages) ── */

export type OuTone = "success" | "warning" | "caution" | "danger" | "info" | "neutral";

export const OuPill: FC<{ tone: OuTone; children?: ReactNode; className?: string; title?: string }> = ({ tone, children, className, title }) => (
  <span className={cx("hs-tag", className)} data-hs-tone={tone} title={title}>
    <span className="hs-tag__dot" aria-hidden="true" />
    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{children}</span>
  </span>
);

export const OuNotice: FC<{ tone?: OuTone; children: ReactNode }> = ({ tone = "info", children }) => (
  <div className="hs-alert" data-hs-tone={tone} role="note" style={{ marginTop: 16 }}>
    <HsInfoIcon style={{ fill: "none", stroke: "var(--hs-tag-dot, currentColor)" }} />
    <span>{children}</span>
  </div>
);

const EmptyWrap = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 4px;
  padding: 40px 16px 32px;
`;

const EmptyIcon = styled("div")`
  width: 56px;
  height: 56px;
  margin-bottom: 12px;
  border-radius: 50%;
  background: var(--hs-fill-hover, #f0f0f0);
  color: var(--hs-text-secondary, #666666);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
    fill: currentColor;
  }
`;

const EmptyTitle = styled("div")`
  font: 600 16px/20px ${HUBSPOT_SANS};
  color: var(--hs-text, #333333);
`;

const EmptyHint = styled("div")`
  font: 300 14px/24px ${HUBSPOT_SANS};
  color: var(--hs-text-secondary, #666666);
  max-width: 420px;
`;

export const OuEmptyState: FC<{ icon: ReactNode; title: ReactNode; hint?: ReactNode }> = ({ icon, title, hint }) => (
  <EmptyWrap>
    <EmptyIcon>{icon}</EmptyIcon>
    <EmptyTitle>{title}</EmptyTitle>
    {hint && <EmptyHint>{hint}</EmptyHint>}
  </EmptyWrap>
);

/* ── skeleton: HubSpot's 16px rounded bar with its white sweep ── */

export const OuBone: FC<SkeletonProps> = ({ variant = "text", width, height, style, className }) => {
  const radius = variant === "circular" ? "50%" : variant === "rectangular" ? 0 : variant === "rounded" ? 6 : 999;
  return (
    <span
      className={cx("hs-bone", className)}
      aria-hidden="true"
      style={{
        width: width ?? "100%",
        height: height ?? (variant === "text" ? 16 : undefined),
        borderRadius: radius,
        display: variant === "text" ? "block" : "inline-block",
        margin: variant === "text" ? "4px 0" : undefined,
        ...style,
      }}
    />
  );
};

/** A loading row: the bordered 16px-cornered box around a bone (activities card). */
export const HsSkeletonRow: FC<{ className?: string } & Record<`data-${string}`, string>> = ({ className, ...rest }) => (
  <article className={cx("hs-skeleton-row", className)} aria-busy="true" {...rest}>
    <span className="hs-bone" />
  </article>
);

/* ── icons ── */

export { RefreshIcon, SyncIcon, QueueIcon, PhoneIcon, ExternalIcon } from "../OutreachUi";
export * from "./icons";
export * from "./pages";

/* ── list pages: search, icon buttons, checkbox, table ── */

/**
 * HubSpot's search input: a pill with the magnifier 16px from the right edge
 * and, once there's text, a clear button in its place. Three sizes, each from
 * its capture (`variant`): "md" the 32px card search (record-activities-card.json),
 * "lg" the 40px settings search (settings-left-panel.json "Search Settings"),
 * "table" the 30px list search (settings-users-page.json).
 */
export const OuSearch: FC<
  Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> & { value: string; onChange: (value: string) => void; width?: number | string; variant?: "md" | "lg" | "table" }
> = ({ value, onChange, placeholder = "Search", width, className, variant = "md", ...rest }) => (
  <div className={cx("hs-search", variant !== "md" && `hs-search--${variant}`, className)} style={{ maxWidth: width ?? 360, minWidth: width ? undefined : 200, width: width ?? undefined }}>
    <input
      type="search"
      role="searchbox"
      value={value}
      placeholder={placeholder}
      aria-label={rest["aria-label"] ?? (typeof placeholder === "string" ? placeholder : undefined)}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Escape" && value) {
          e.stopPropagation();
          onChange("");
        }
      }}
      {...rest}
    />
    {value ? (
      <button type="button" className="hs-search__clear hs-focusable" aria-label="Clear search" onClick={() => onChange("")}>
        <HsCloseIcon strokeWidth={3} />
      </button>
    ) : (
      <span className="hs-search__icon" aria-hidden="true">
        <HsSearchIcon />
      </span>
    )}
  </div>
);

/** A 32px circle (24px in card headers); with a label, a small secondary pill. */
export const OuIconButton: FC<MuiIconButtonProps & { label?: ReactNode; pressed?: boolean; xs?: boolean }> = (props) => {
  const { label, children, className, disabled, pressed, xs } = props;
  return (
    <button
      {...buttonAttrs(props as OuButtonProps)}
      className={cx("hs-btn", "hs-btn--secondary", label == null && "hs-btn--icon", xs && "hs-btn--xs", "hs-focusable", className)}
      disabled={disabled}
      aria-pressed={pressed}
    >
      {children}
      {label != null && <span className="hs-btn__label">{label}</span>}
    </button>
  );
};

/** A record action: the circle button with its label underneath. */
export const HsRecordAction: FC<{ icon: ReactNode; label: ReactNode; onClick?: () => void; disabled?: boolean } & Record<`data-${string}`, string>> = ({
  icon,
  label,
  onClick,
  disabled,
  ...rest
}) => (
  <div className="hs-action" {...rest}>
    <button type="button" className="hs-btn hs-btn--secondary hs-btn--icon hs-focusable" onClick={onClick} disabled={disabled} aria-label={typeof label === "string" ? label : undefined}>
      {icon}
    </button>
    <div className="hs-action__label" aria-hidden="true">
      {label}
    </div>
  </div>
);

export const OuCheckbox: FC<{ checked: boolean; indeterminate?: boolean; onChange: (checked: boolean) => void; "aria-label"?: string; disabled?: boolean }> = ({
  checked,
  indeterminate,
  onChange,
  "aria-label": ariaLabel,
  disabled,
}) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <label className="hs-check" onClick={(e) => e.stopPropagation()}>
      <input ref={ref} type="checkbox" checked={checked} disabled={disabled} aria-label={ariaLabel} onChange={(e) => onChange(e.target.checked)} />
      <span className="hs-check__box" aria-hidden="true">
        <span className="hs-check__icon hs-check__icon--on">
          <HsCheckIcon />
        </span>
        <span className="hs-check__icon hs-check__icon--mixed">
          <HsDashIcon />
        </span>
      </span>
    </label>
  );
};

export const OuResultCount = styled("span")`
  font: 300 14px/24px ${HUBSPOT_SANS};
  color: var(--hs-text-secondary, #666666);
  white-space: nowrap;
`;

export interface OuTableColumn<T> {
  key: string;
  header: ReactNode;
  /** Pixels; the table is laid out fixed and scrolls sideways past its container. */
  width: number;
  render: (row: T) => ReactNode;
  /** Stays put on horizontal scroll. Only the first column should set it. */
  sticky?: boolean;
  align?: "left" | "center";
}

const CHECK_COL = 45;

export interface OuTableProps<T> {
  columns: OuTableColumn<T>[];
  rows: T[];
  getRowId: (row: T) => string;
  selection?: { selected: Set<string>; onChange: (next: Set<string>) => void };
  loading?: boolean;
  skeletonRows?: number;
  pageSize?: number;
}

/**
 * HubSpot's index table (index-table.json): white, 40px rows with #CCC rules
 * between them, #666 14px column labels, 2px 24px cells, #E6E6E6 row hover,
 * and HubSpot's numbered pager underneath once there's more than a page.
 */
export function OuTable<T>({ columns, rows, getRowId, selection, loading, skeletonRows = 8, pageSize = 100 }: OuTableProps<T>): JSX.Element {
  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [rows, pageSize]);

  const pages = Math.max(1, Math.ceil(rows.length / pageSize));
  const shown = rows.slice(page * pageSize, page * pageSize + pageSize);
  const ids = rows.map(getRowId);
  const allSelected = !!selection && ids.length > 0 && ids.every((id) => selection.selected.has(id));
  const someSelected = !!selection && ids.some((id) => selection.selected.has(id));
  const offset = selection ? CHECK_COL : 0;
  const width = offset + columns.reduce((sum, c) => sum + c.width, 0);

  const stickyStyle = (c: OuTableColumn<T>) => (c.sticky ? { left: offset } : undefined);
  const cellClass = (c: OuTableColumn<T>) => cx(c.sticky && "hs-sticky", c.align === "center" && "hs-center") || undefined;

  return (
    <>
      <div className="hs-table-scroll">
        <table className="hs-table" style={{ width, minWidth: "100%" }}>
          <colgroup>
            {selection && <col style={{ width: CHECK_COL }} />}
            {columns.map((c) => (
              <col key={c.key} style={{ width: c.width }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {selection && (
                <th className="hs-table__check hs-sticky" style={{ left: 0 }}>
                  <OuCheckbox
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    aria-label="Select all"
                    onChange={(on) => selection.onChange(on ? new Set(ids) : new Set())}
                  />
                </th>
              )}
              {columns.map((c) => (
                <th key={c.key} scope="col" className={cellClass(c)} style={stickyStyle(c)}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: skeletonRows }, (_, i) => (
                  <tr key={`bone-${i}`} aria-hidden="true">
                    {selection && (
                      <td className="hs-table__check hs-sticky" style={{ left: 0 }}>
                        <OuBone variant="rounded" width={20} height={20} style={{ verticalAlign: "middle" }} />
                      </td>
                    )}
                    {columns.map((c, j) => (
                      <td key={c.key} className={cellClass(c)} style={stickyStyle(c)}>
                        <OuBone variant="text" width={`${[70, 55, 80, 45, 65][(i + j) % 5]}%`} />
                      </td>
                    ))}
                  </tr>
                ))
              : shown.map((row) => {
                  const id = getRowId(row);
                  return (
                    <tr key={id}>
                      {selection && (
                        <td className="hs-table__check hs-sticky" style={{ left: 0 }}>
                          <OuCheckbox
                            checked={selection.selected.has(id)}
                            aria-label="Select row"
                            onChange={(on) => {
                              const next = new Set(selection.selected);
                              if (on) next.add(id);
                              else next.delete(id);
                              selection.onChange(next);
                            }}
                          />
                        </td>
                      )}
                      {columns.map((c) => (
                        <td key={c.key} className={cellClass(c)} style={stickyStyle(c)}>
                          {c.render(row)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>
      {!loading && pages > 1 && (
        <div style={{ padding: "16px 0 8px" }}>
          <HsPagination page={page} pages={pages} onChange={setPage} />
        </div>
      )}
    </>
  );
}

/* ── pagination (pagination.json) ── */

export const HsPagination: FC<{ page: number; pages: number; onChange: (page: number) => void; maxNumbers?: number } & Record<`data-${string}`, string>> = ({
  page,
  pages,
  onChange,
  maxNumbers = 11,
  ...rest
}) => {
  const start = Math.max(0, Math.min(page - Math.floor(maxNumbers / 2), pages - maxNumbers));
  const numbers = Array.from({ length: Math.min(maxNumbers, pages) }, (_, i) => start + i);
  return (
    <nav className="hs-pager" aria-label="Pagination" {...rest}>
      <span>
        <button type="button" className="hs-btn hs-btn--transparent hs-focusable" disabled={page === 0} onClick={() => onChange(page - 1)}>
          <HsLeftIcon />
          <span>Prev</span>
        </button>
      </span>
      <span className="hs-pager__numbers">
        {numbers.map((n) => (
          <button
            key={n}
            type="button"
            className="hs-btn hs-btn--transparent hs-focusable"
            aria-current={n === page ? "page" : undefined}
            aria-label={`Page ${n + 1}`}
            onClick={() => onChange(n)}
          >
            {n + 1}
          </button>
        ))}
      </span>
      <span>
        <button type="button" className="hs-btn hs-btn--transparent hs-focusable" disabled={page >= pages - 1} onClick={() => onChange(page + 1)}>
          <span>Next</span>
          <HsRightIcon />
        </button>
      </span>
    </nav>
  );
};

/* ── tabs (record-middle-tabs-header.json) ── */

export interface HsTabOption {
  value: string;
  label: ReactNode;
  disabled?: boolean;
}

/**
 * HubSpot's tabs. "boxed" is the record page's tab strip (grey tabs in a
 * #CCC frame, the current one white and open into the panel); "underline"
 * is the plain strip with the 5px bar under the current tab.
 */
export const HsTabs: FC<{
  options: HsTabOption[];
  value: string;
  onChange: (value: string) => void;
  variant?: "boxed" | "underline";
  fill?: boolean;
  className?: string;
  "aria-label"?: string;
} & Record<`data-${string}`, string>> = ({ options, value, onChange, variant = "boxed", fill, className, "aria-label": ariaLabel, ...rest }) => (
  <div className={cx("hs-tabs", variant === "underline" && "hs-tabs--underline", fill && "hs-tabs--fill", className)} {...rest}>
    <div className="hs-tabs__list" role="tablist" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          className="hs-tab"
          aria-selected={o.value === value}
          tabIndex={o.value === value ? 0 : -1}
          disabled={o.disabled}
          onClick={() => onChange(o.value)}
        >
          {o.label}
          <span className="hs-tab__indicator" aria-hidden="true" />
        </button>
      ))}
    </div>
  </div>
);

/** View tabs (view-tabs.json): 32px chips, the current one on #EBEBEB. */
export const HsViewTabs: FC<{ options: HsTabOption[]; value: string; onChange: (value: string) => void; icon?: ReactNode; className?: string } & Record<`data-${string}`, string>> = ({
  options,
  value,
  onChange,
  icon,
  className,
  ...rest
}) => (
  <div className={cx("hs-views", className)} role="tablist" {...rest}>
    {options.map((o) => (
      <div key={o.value} className="hs-views__item" data-selected={o.value === value ? "true" : undefined}>
        <button type="button" role="tab" className="hs-view hs-focusable" aria-selected={o.value === value} disabled={o.disabled} onClick={() => onChange(o.value)}>
          {icon}
          {o.label}
        </button>
      </div>
    ))}
  </div>
);

/* ── cards (record-*-card.json) ── */

export const HsCard: FC<{ title?: ReactNode; toolbar?: ReactNode; children?: ReactNode; className?: string; style?: CSSProperties } & Record<`data-${string}`, string>> = ({
  title,
  toolbar,
  children,
  className,
  style,
  ...rest
}) => (
  <section className={cx("hs-card", className)} style={style} {...rest}>
    {(title || toolbar) && (
      <div className="hs-card__header">
        <h3 className="hs-card__title">{title}</h3>
        {toolbar && <div className="hs-card__toolbar">{toolbar}</div>}
      </div>
    )}
    <div className="hs-card__section">{children}</div>
  </section>
);

/** Data highlights: centred uppercase 12px labels over their values. */
export const HsHighlights: FC<{ items: { label: ReactNode; value: ReactNode }[] }> = ({ items }) => (
  <ul className="hs-highlights">
    {items.map((it, i) => (
      <li key={i}>
        <p className="hs-highlights__label">{it.label}</p>
        <p className="hs-highlights__value">{it.value}</p>
      </li>
    ))}
  </ul>
);

/* ── tooltip (tooltip.json) ── */

/** HubSpot's tooltip: #141414, 12/18 white, 6px corners, the rounded arrow. */
export const OuTooltip: FC<MuiTooltipProps> = ({ children, slotProps, ...rest }) => (
  <MuiTooltip
    arrow
    enterDelay={300}
    {...rest}
    slotProps={{
      ...slotProps,
      tooltip: { ...slotProps?.tooltip, className: cx("hs-mui-tooltip", (slotProps?.tooltip as { className?: string } | undefined)?.className) },
      popper: { ...slotProps?.popper, className: "hs-mui-tooltip-popper", modifiers: [{ name: "offset", options: { offset: [0, 4] } }] },
    }}
  >
    {children}
  </MuiTooltip>
);

/* ── dialog (modal-dialog.json, modal-overlay-root.json) ── */

/**
 * HubSpot's modal motion on MUI's Dialog: the dialog fades in and grows from
 * 90% over 250ms after a 100ms delay (HubSpot's ModalTransition rules), and
 * leaves the same way. The overlay is MUI's backdrop in HubSpot's 46% ink.
 */
export const HsDialogTransition = forwardRef<HTMLElement, { in?: boolean; appear?: boolean; children: ReactElement; onEnter?: (node: HTMLElement, isAppearing: boolean) => void; onEntered?: (node: HTMLElement, isAppearing: boolean) => void; onExit?: (node: HTMLElement) => void; onExited?: (node: HTMLElement) => void; timeout?: unknown }>(
  function HsDialogTransition({ in: inProp, appear = true, children, onEnter, onEntered, onExit, onExited, ...rest }, ref) {
    const nodeRef = useRef<HTMLElement | null>(null);
    const setRef = (node: HTMLElement | null) => {
      nodeRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLElement | null>).current = node;
    };
    const styleFor = (state: TransitionStatus): CSSProperties => ({ opacity: 1, visibility: state === "exited" && !inProp ? "hidden" : undefined });
    return (
      <Transition
        appear={appear}
        in={inProp}
        nodeRef={nodeRef}
        timeout={{ enter: 350, exit: 350 }}
        onEnter={(isAppearing: boolean) => {
          const node = nodeRef.current;
          if (node) {
            node.setAttribute("data-hs-motion", "from");
            void node.offsetHeight; // reflow so the transition starts from 90%
            onEnter?.(node, isAppearing);
          }
        }}
        onEntering={() => nodeRef.current?.setAttribute("data-hs-motion", "in")}
        onEntered={(isAppearing: boolean) => nodeRef.current && onEntered?.(nodeRef.current, isAppearing)}
        onExit={() => {
          if (nodeRef.current) {
            nodeRef.current.setAttribute("data-hs-motion", "out");
            onExit?.(nodeRef.current);
          }
        }}
        onExited={() => nodeRef.current && onExited?.(nodeRef.current)}
        {...(rest as object)}
      >
        {(state: TransitionStatus) =>
          React.cloneElement(children, {
            ref: setRef,
            style: { ...styleFor(state), ...(children.props as { style?: CSSProperties }).style },
          })
        }
      </Transition>
    );
  }
);

/** A HubSpot modal: header with the title and close ×, body, footer of pills. */
export const HsDialog: FC<
  Omit<MuiDialogProps, "title"> & { title: ReactNode; footer?: ReactNode; onClose?: () => void; width?: number; paperProps?: Record<string, string> }
> = ({ title, footer, children, onClose, width = 640, paperProps, PaperProps, ...rest }) => (
  <MuiDialog
    onClose={onClose}
    maxWidth={false}
    TransitionComponent={HsDialogTransition as unknown as MuiDialogProps["TransitionComponent"]}
    {...rest}
    PaperProps={{ ...PaperProps, ...paperProps, className: cx("hs-dialog", "hs-mui-dialog", PaperProps?.className), elevation: 0, style: { width, margin: 16, ...PaperProps?.style } }}
  >
    <header className="hs-dialog__header">
      <div className="hs-dialog__header-inner">
        <h4 className="hs-dialog__title">{title}</h4>
        {onClose && (
          <button type="button" className="hs-dialog__close hs-focusable" aria-label="Close" onClick={onClose}>
            <HsCloseIcon strokeWidth={2.6} />
          </button>
        )}
      </div>
    </header>
    <div className="hs-dialog__body">{children}</div>
    {footer && <footer className="hs-dialog__footer">{footer}</footer>}
  </MuiDialog>
);
export * from "./DateRangeSelect";
